import React, { useState } from 'react';
import { Collection, CollectionStatus } from '../../types/collections';
// Import icons from the new location
import { CopyIcon, HashIcon, CheckIcon } from '../icons/Icons.tsx';

interface CollectionTableProps {
  collections: Collection[];
  // Props for ID copy functionality
  copiedId: string | null;
  hoveredIdIcon: string | null;
  setHoveredIdIcon: (id: string | null) => void;
  onCopyId: (id: string) => void;
  // New prop for row click handling
  onRowClick?: (collection: Collection) => void;
}

// Status Badge Component (mimics PartnerDetailsPage styling)
const StatusBadge: React.FC<{ status: CollectionStatus }> = ({ status }) => {
  let styles: React.CSSProperties = {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    textTransform: 'capitalize',
    display: 'inline-block',
  };

  switch (status) {
    case 'Pending':
      styles = { ...styles, backgroundColor: '#fefce8', color: '#ca8a04' }; // Yellow
      break;
    case 'Sent':
      styles = { ...styles, backgroundColor: '#eff6ff', color: '#3b82f6' }; // Blue
      break;
    case 'Paid':
      styles = { ...styles, backgroundColor: '#ecfdf5', color: '#059669' }; // Green
      break;
    case 'Overdue':
      styles = { ...styles, backgroundColor: '#fef2f2', color: '#dc2626' }; // Red
      break;
    default:
      styles = { ...styles, backgroundColor: '#f8fafc', color: '#64748b' }; // Default gray
      break;
  }

  return <span style={styles}>{status}</span>;
};

// Helper to format date/time like 'Apr 30, 2025, 8:40 am' (lowercase am/pm)
const formatDateTime = (isoString: string): { date: string, time: string } => {
  try {
    const dateObj = new Date(isoString);
    // Format Date: e.g., Mar 31, 2025
    const formattedDate = dateObj.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    });
    // Format Time: e.g., 4:00 AM (lowercase am/pm)
    const formattedTime = dateObj.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).toLowerCase(); // Convert AM/PM to lowercase

    return { date: formattedDate, time: formattedTime };
  } catch (error) {
    console.error("Error formatting date:", isoString, error);
    return { date: 'Invalid Date', time: '' }; // Fallback for invalid dates
  }
};

// Helper to format currency like 'AED 2,500.00'
const formatCurrency = (amount: number, currency: string): string => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  // Let the formatter handle the currency symbol/code placement
  return formatter.format(amount);
};

const CollectionTable: React.FC<CollectionTableProps> = ({ 
  collections, 
  copiedId, 
  hoveredIdIcon, 
  setHoveredIdIcon, 
  onCopyId,
  onRowClick
}) => {
  // State for row hover effect
  const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

  // Helper to handle row click without triggering when clicking ID copy button
  const handleRowClick = (collection: Collection, event: React.MouseEvent) => {
    // Skip if clicking on ID column
    if ((event.target as Element).closest('td:first-child')) {
      return;
    }
    
    // Call the onRowClick handler if provided
    if (onRowClick) {
      onRowClick(collection);
    }
  };

  return (
    <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' /* Match Transactions page container */ }}>
      {/* Table */} 
      {collections.length > 0 ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' /* Match Transactions font size */ }}>
          <thead style={{ backgroundColor: '#f9fafb' /* Match Transactions header bg */ }}>
            <tr style={{ borderBottom: '1px solid #e5e7eb', textAlign: 'left', color: '#6b7280' /* Match Transactions header text color */ }}>
              {/* --- Columns --- */} 
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center', width: '60px' }}>ID</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Created at</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Partner</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Country</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'left' }}>Product</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'right' }}>Amount</th>
              <th style={{ fontWeight: 500, padding: '12px 16px', textAlign: 'center' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {collections.map((collection, index) => {
              const { date, time } = formatDateTime(collection.createdAt);
              return (
                <tr
                  key={collection.id}
                  style={{
                    borderBottom: '1px solid #f3f4f6', // Match Transactions row border
                    cursor: 'pointer',
                    backgroundColor: hoveredRowIndex === index ? '#f9fafb' : 'transparent', // Match Transactions hover bg
                    transition: 'background-color 0.1s ease-in-out',
                    height: '35px' // Match Transactions row height
                  }}
                  onMouseEnter={() => setHoveredRowIndex(index)}
                  onMouseLeave={() => setHoveredRowIndex(null)}
                  onClick={(e) => handleRowClick(collection, e)} // Add click handler
                  data-collection-row="true"
                >
                  {/* --- Cells --- */} 
                  {/* ID Cell with Copy functionality */}
                  <td style={{ padding: '7px 16px', width: '60px', textAlign: 'center' }}>
                     <div
                       style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', height: '100%', position: 'relative' }}
                       onClick={() => onCopyId(collection.id)}
                       title={copiedId === collection.id ? 'ID copied' : `Click to copy ID: ${collection.id}`}
                       onMouseEnter={() => setHoveredIdIcon(collection.id)}
                       onMouseLeave={() => setHoveredIdIcon(null)}
                     >
                       {
                         copiedId === collection.id ? (
                           <CheckIcon style={{ color: '#10b981' }} size={16} /> // Use CheckIcon for feedback
                         ) : hoveredIdIcon === collection.id ? (
                           <CopyIcon style={{ color: '#6b7280' }} size={14} /> // Show CopyIcon on hover
                         ) : (
                           <HashIcon style={{ color: '#9ca3af' }} size={14} /> // Default HashIcon
                         )
                       }
                     </div>
                  </td>
                  {/* Created at Cell */}
                  <td style={{ padding: '7px 16px', color: '#374151', whiteSpace: 'nowrap', width: '140px', fontWeight: 400, textAlign: 'left' }}>
                      {`${date}, ${time}`}
                  </td>
                  {/* Partner Cell */}
                  <td style={{ padding: '7px 16px', textAlign: 'left' }}>
                    <div style={{ fontWeight: 400, color: '#111827' }}>{collection.partnerName}</div>
                  </td>
                  {/* Country Cell */}
                  <td style={{ padding: '7px 16px', color: '#374151', width: '80px', textAlign: 'left' }}>{collection.country}</td>
                  {/* Product Cell */}
                  <td style={{ padding: '7px 16px', textAlign: 'left' }}>
                     <span style={{
                        backgroundColor: '#f3f4f6', // Match Transactions product badge style
                        color: '#374151',
                        padding: '3px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        display: 'inline-block',
                     }}>
                         {collection.product}
                     </span>
                  </td>
                  {/* Amount Cell */}
                  <td style={{ padding: '7px 16px', fontWeight: 500, color: collection.amount < 0 ? '#ef4444' : '#111827' /* Use red for negative */, textAlign: 'right' }}>
                    {formatCurrency(collection.amount, collection.currency)}
                  </td>
                  {/* Status Cell */}
                  <td style={{ padding: '7px 16px', textAlign: 'center' }}>
                    <StatusBadge status={collection.status} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
       ) : (
          // Empty State (matches TransactionsPage)
         <div style={{ 
           textAlign: 'center', 
           padding: '40px 20px', 
           color: '#6b7280', 
           borderTop: '1px solid #e5e7eb', // Show border only when table would be there
           // Removed margin from TransactionsPage empty state as this div is inside the main container
         }}>
            {/* You can add an SVG icon here if desired, similar to TransactionsPage */}
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="#d1d5db" style={{ width: '48px', height: '48px', marginBottom: '16px' }}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
            </svg>
           <p style={{ fontSize: '16px', fontWeight: 500, color: '#111827', margin: '0 0 4px 0' }}>No collections found</p>
           <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>Try adjusting your filter criteria.</p>
         </div>
        )}
    </div>
  );
};

export default CollectionTable; 